import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Camera, X, Scan, AlertCircle, Loader2, Volume2, Target, RefreshCw } from "lucide-react";
import { useScanditScanner } from "@/hooks/useScanditScanner";
import { ScannerControls } from "./ScannerControls";
import { ScannerOverlay } from "./ScannerOverlay";
import { cleanBarcodeData } from "@/components/scanner-test/utils/barcodeUtils";

interface BarcodeScannerProps {
  onScanResult: (barcode: string) => void;
  onClose: () => void;
}

export const BarcodeScanner = ({ onScanResult, onClose }: BarcodeScannerProps) => {
  const {
    isScanning,
    error,
    isInitialized,
    torchEnabled,
    detectionStrength,
    soundEnabled,
    initializationStep,
    retryCount,
    videoRef,
    startScanning,
    stopScanning,
    toggleTorch,
    toggleSound,
    simulateScan,
    retryInitialization
  } = useScanditScanner({ 
    onScanResult: (barcode: string) => {
      // Apply enhanced cleaning for Scandit results
      const cleanedBarcode = cleanBarcodeData(barcode, 'Scandit');
      onScanResult(cleanedBarcode || barcode);
    }, 
    onClose 
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md">
        <CardContent className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Scan className="w-5 h-5" />
              Scandit Scanner
            </h3>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleSound}
                className={`p-1 ${soundEnabled ? 'text-green-600' : 'text-gray-400'}`}
                title={soundEnabled ? 'Sound enabled' : 'Sound disabled'}
              >
                <Volume2 className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={onClose}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          {!isInitialized && (
            <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-lg flex items-center gap-2">
              <Loader2 className="w-5 h-5 text-purple-500 animate-spin flex-shrink-0" />
              <div>
                <p className="text-sm text-purple-700 font-medium">Loading Scandit Scanner</p>
                <p className="text-xs text-purple-600">{initializationStep || 'Initializing enterprise barcode detection...'}</p>
              </div>
            </div>
          )}

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <div>
                <p className="text-sm text-red-700 font-medium">Scanner Error</p>
                <p className="text-xs text-red-600">{error}</p>
              </div>
            </div>
          )}

          {retryCount > 0 && (
            <div className="mb-4 p-2 bg-purple-50 border border-purple-200 rounded-lg text-xs text-purple-700">
              Retry attempts: {retryCount}
            </div>
          )}

          {detectionStrength > 0 && isScanning && (
            <div className="mb-4 space-y-2">
              <div className="flex justify-between items-center text-xs">
                <span className="flex items-center gap-1">
                  <Target className="w-3 h-3 text-purple-500" />
                  Detection Strength
                </span>
                <span className="font-mono text-purple-600">{Math.round(detectionStrength)}%</span>
              </div>
              <Progress value={detectionStrength} className="h-2" />
            </div>
          )}
          
          {!isScanning ? (
            <div className="text-center space-y-4">
              <Camera className="w-16 h-16 mx-auto text-gray-400" />
              <div>
                <p className="text-gray-600 mb-2">Enterprise barcode scanning powered by Scandit</p>
                <p className="text-xs text-gray-500">
                  High accuracy detection with real-time processing and soft audio feedback
                </p>
              </div>
              <div className="space-y-2">
                <Button 
                  onClick={startScanning} 
                  className="w-full bg-purple-600 hover:bg-purple-700" 
                  disabled={!isInitialized || !!error}
                >
                  <Scan className="w-4 h-4 mr-2" />
                  Start Scanner
                </Button>
                
                {!isInitialized && (
                  <Button 
                    onClick={retryInitialization}
                    variant="outline" 
                    className="w-full text-purple-600"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Retry Initialization {retryCount > 0 && `(#${retryCount + 1})`}
                  </Button>
                )}
                
                <Button onClick={simulateScan} variant="outline" className="w-full">
                  Simulate Scan (Demo)
                </Button>
              </div>
              <div className="text-xs text-gray-500 flex items-center justify-center gap-1">
                <Volume2 className="w-3 h-3" />
                Sound: {soundEnabled ? 'Enabled (Soft)' : 'Disabled'}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="text-center mb-2">
                <p className="text-sm text-gray-600 flex items-center justify-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Scandit Scanner Active
                </p>
                <p className="text-xs text-gray-500">Point your camera at a barcode</p>
              </div>
              <ScannerOverlay videoRef={videoRef} />
              <ScannerControls
                isScanning={isScanning}
                torchEnabled={torchEnabled}
                onSimulateScan={simulateScan}
                onToggleTorch={toggleTorch}
                onStopScanning={stopScanning}
              />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
